import React, { useMemo, useState } from 'react';
import {
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import RoutePaths, { ALERTS_MONITORING_ID } from '../routes/RoutePaths';
import DisabledSourceChip from './DisabledSourceChip';
import SortableTableHeaderCell from './SortableTableHeaderCell';
import { applySortDirection, compareDates, compareNumbers, compareStrings } from './tableSort';

const COLUMNS = [
    { id: 'source', labelId: 'monitoring.alerts.column.source' },
    {
        id: 'type',
        labelId: 'monitoring.alerts.column.type',
        wrap: true,
        headerMaxWidth: 88,
    },
    {
        id: 'streak',
        labelId: 'monitoring.alerts.column.streak',
        align: 'right',
        wrap: true,
        headerMaxWidth: 92,
    },
    {
        id: 'firstFailure',
        labelId: 'monitoring.alerts.column.firstFailure',
        wrap: true,
        headerMaxWidth: 88,
    },
    {
        id: 'lastFailure',
        labelId: 'monitoring.alerts.column.lastFailure',
        wrap: true,
        headerMaxWidth: 88,
    },
    {
        id: 'lastSuccess',
        labelId: 'monitoring.alerts.column.lastSuccess',
        wrap: true,
        headerMaxWidth: 96,
    },
    {
        id: 'lastAlerted',
        labelId: 'monitoring.alerts.column.lastAlerted',
        wrap: true,
        headerMaxWidth: 96,
    },
    {
        id: 'suppress',
        labelId: 'monitoring.alerts.column.suppress',
        wrap: true,
        headerMaxWidth: 80,
    },
    {
        id: 'error',
        labelId: 'monitoring.alerts.column.error',
        wrap: true,
        headerMaxWidth: 88,
    },
    { id: 'actions', labelId: 'monitoring.alerts.column.actions', sortable: false },
];

const STICKY_SOURCE_BODY_SX = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    minWidth: 160,
    maxWidth: 240,
    boxShadow: '4px 0 8px -6px rgba(0, 0, 0, 0.18)',
    'tbody tr:hover &': {
        bgcolor: 'action.hover',
    },
};

const STICKY_SOURCE_HEADER_SX = {
    ...STICKY_SOURCE_BODY_SX,
    zIndex: 3,
};

const COMPACT_CELL_SX = {
    whiteSpace: 'nowrap',
    px: 1,
};

function isStaleAlert(alert) {
    return alert?.alert_type === 'stale_content';
}

function formatDurationSeconds(seconds, locale) {
    if (typeof seconds !== 'number') {
        return '–';
    }
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const isFinnish = String(locale).toLowerCase().startsWith('fi');
    const dayLabel = isFinnish ? 'pv' : 'd';

    if (days > 0 && hours > 0) {
        return `${days} ${dayLabel} ${hours} h`;
    }
    if (days > 0) {
        return `${days} ${dayLabel}`;
    }
    if (hours > 0) {
        return `${hours} h`;
    }
    if (minutes > 0) {
        return `${minutes} min`;
    }
    return `${seconds} s`;
}

function formatDateTime(value, locale) {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '–';
    return date.toLocaleString(locale ?? 'fi-FI', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

function isAlertSuppressed(alert, now = Date.now()) {
    if (!alert?.suppress_until) {
        return false;
    }
    const until = new Date(alert.suppress_until).getTime();
    return Number.isFinite(until) && until > now;
}

function getSourceDetailPath(sourceId) {
    return RoutePaths.monitoring.source
        .replace(':mediaId', encodeURIComponent(ALERTS_MONITORING_ID))
        .replace(':sourceId', encodeURIComponent(sourceId));
}

function compareAlertRows(a, b, orderBy) {
    switch (orderBy) {
        case 'type':
            return compareStrings(a?.alert_type, b?.alert_type);
        case 'streak':
            if (isStaleAlert(a) || isStaleAlert(b)) {
                return compareNumbers(a?.failing_for_seconds, b?.failing_for_seconds);
            }
            return compareNumbers(a?.fail_streak, b?.fail_streak);
        case 'firstFailure':
            return compareDates(a?.first_failure_at, b?.first_failure_at);
        case 'lastFailure':
            return compareDates(a?.last_failure_at, b?.last_failure_at);
        case 'lastSuccess':
            return compareDates(a?.last_success_at, b?.last_success_at);
        case 'lastAlerted':
            return compareDates(a?.last_alerted_at, b?.last_alerted_at);
        case 'suppress':
            return compareDates(a?.suppress_until, b?.suppress_until);
        case 'error':
            return compareStrings(a?.last_error_summary, b?.last_error_summary);
        case 'source':
        default:
            return compareStrings(a?.source_id, b?.source_id);
    }
}

function DateTimeCell({ value, locale }) {
    return (
        <TableCell sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
            <Typography variant="body2" color="text.secondary" noWrap>
                {formatDateTime(value, locale)}
            </Typography>
        </TableCell>
    );
}

function AlertTypeCell({ alert, intl }) {
    const stale = isStaleAlert(alert);
    const labelId = stale
        ? 'monitoring.alerts.type.stale'
        : 'monitoring.alerts.type.failure';
    const color = stale ? 'warning' : 'error';

    return (
        <TableCell sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
            <Chip size="small" label={intl.formatMessage({ id: labelId })} color={color} variant="outlined" />
        </TableCell>
    );
}

function MetricCell({ alert, locale }) {
    const stale = isStaleAlert(alert);
    const value = stale
        ? formatDurationSeconds(alert?.failing_for_seconds, locale)
        : (alert?.fail_streak ?? '–');

    return (
        <TableCell align="right" sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
            <Typography variant="body2">{value}</Typography>
        </TableCell>
    );
}

function ErrorMessageCell({ value }) {
    const errorText = value ?? '';

    return (
        <TableCell
            sx={{ borderColor: 'divider', maxWidth: 180, overflow: 'hidden', ...COMPACT_CELL_SX }}
        >
            <Tooltip title={errorText} arrow disableHoverListener={!errorText}>
                <Typography
                    variant="body2"
                    color="error.main"
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {errorText}
                </Typography>
            </Tooltip>
        </TableCell>
    );
}

function SuppressStatusCell({ alert, locale, intl }) {
    const suppressed = isAlertSuppressed(alert);
    if (!suppressed) {
        return (
            <Typography variant="body2" color="text.secondary">
                –
            </Typography>
        );
    }

    const tooltipLines = [
        intl.formatMessage(
            { id: 'monitoring.alerts.suppress.until' },
            { date: formatDateTime(alert.suppress_until, locale) }
        ),
    ];
    if (alert.suppressed_by) {
        tooltipLines.push(
            intl.formatMessage(
                { id: 'monitoring.alerts.suppress.byUser' },
                { userName: alert.suppressed_by }
            )
        );
    }

    return (
        <Tooltip title={tooltipLines.join('\n')} arrow>
            <Chip
                size="small"
                label={intl.formatMessage({ id: 'monitoring.alerts.suppress.active' })}
                color="default"
                variant="outlined"
            />
        </Tooltip>
    );
}

export default function AlertsTable({ alerts, onSuppressAlert }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState('lastFailure');
    const [order, setOrder] = useState('desc');

    const handleSort = (columnId) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const sortedAlerts = useMemo(() => {
        return [...(alerts ?? [])].sort((a, b) => {
            const result = compareAlertRows(a, b, orderBy);
            if (result !== 0) {
                return applySortDirection(result, order);
            }
            return compareStrings(a?.source_id, b?.source_id);
        });
    }, [alerts, order, orderBy]);

    const headerCellSx = {
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        fontSize: '0.75rem',
        whiteSpace: 'normal',
        lineHeight: 1.25,
        verticalAlign: 'bottom',
        px: 1,
    };

    const columnLabel = (id) => intl.formatMessage({ id: `monitoring.alerts.column.${id}` });

    return (
        <TableContainer
            sx={{
                height: '100%',
                width: '100%',
                overflow: 'auto',
            }}
        >
            <Table
                stickyHeader
                size="small"
                sx={{ minWidth: 1260 }}
                aria-label={intl.formatMessage({ id: 'monitoring.alerts.title' })}
            >
                <TableHead>
                    <TableRow>
                        {COLUMNS.map((column) =>
                            column.sortable === false ? (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={headerCellSx}
                                >
                                    {column.id === 'actions' ? '' : columnLabel(column.id)}
                                </TableCell>
                            ) : (
                                <SortableTableHeaderCell
                                    key={column.id}
                                    columnId={column.id}
                                    label={columnLabel(column.id)}
                                    wrap={column.wrap}
                                    orderBy={orderBy}
                                    order={order}
                                    onSort={handleSort}
                                    align={column.align}
                                    sx={{
                                        ...(column.id === 'source'
                                            ? { ...headerCellSx, ...STICKY_SOURCE_HEADER_SX }
                                            : headerCellSx),
                                        ...(column.headerMaxWidth
                                            ? { maxWidth: column.headerMaxWidth }
                                            : {}),
                                    }}
                                />
                            )
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedAlerts.map((alert) => {
                        const suppressed = isAlertSuppressed(alert);
                        const actionLabelId = suppressed
                            ? 'monitoring.alerts.suppress.editAction'
                            : 'monitoring.alerts.suppress.action';
                        const ActionIcon = suppressed ? EditOutlinedIcon : NotificationsOffOutlinedIcon;

                        return (
                            <TableRow
                                key={alert.id ?? alert.source_id}
                                hover
                                onClick={() => navigate(getSourceDetailPath(alert.source_id))}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell
                                    sx={{
                                        borderColor: 'divider',
                                        ...STICKY_SOURCE_BODY_SX,
                                        ...COMPACT_CELL_SX,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                            {alert.source_id}
                                        </Typography>
                                        {alert.enabled === false && <DisabledSourceChip />}
                                    </Stack>
                                </TableCell>
                                <AlertTypeCell alert={alert} intl={intl} />
                                <MetricCell alert={alert} locale={intl.locale} />
                                <DateTimeCell value={alert.first_failure_at} locale={intl.locale} />
                                <DateTimeCell value={alert.last_failure_at} locale={intl.locale} />
                                <DateTimeCell value={alert.last_success_at} locale={intl.locale} />
                                <DateTimeCell value={alert.last_alerted_at} locale={intl.locale} />
                                <TableCell sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
                                    <SuppressStatusCell
                                        alert={alert}
                                        locale={intl.locale}
                                        intl={intl}
                                    />
                                </TableCell>
                                <ErrorMessageCell value={alert.last_error_summary} />
                                <TableCell sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }} align="right">
                                    <Tooltip title={intl.formatMessage({ id: actionLabelId })} arrow>
                                        <IconButton
                                            size="small"
                                            aria-label={intl.formatMessage({ id: actionLabelId })}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onSuppressAlert?.(alert);
                                            }}
                                        >
                                            <ActionIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
